import { create } from "zustand";
import type { Item, MessageItem, MessageContent } from "@/lib/assistant";
import type { ChatCompletionMessageParam } from "openai/resources/chat";

interface ConversationState {
  conversationItems: ChatCompletionMessageParam[];
  chatMessages: Item[];
  addConversationItem: (item: ChatCompletionMessageParam) => void;
  addChatMessage: (item: Item) => void;
  updateAssistantMessage: (content: string) => void;
}

const useConversationStore = create<ConversationState>((set) => ({
  conversationItems: [],
  chatMessages: [],
  addConversationItem: (item) =>
    set((state) => ({
      conversationItems: [...state.conversationItems, item],
    })),
  addChatMessage: (item) =>
    set((state) => ({
      chatMessages: [...state.chatMessages, item],
    })),
  updateAssistantMessage: (content: string) =>
    set((state) => {
      const newConversationItems = [...state.conversationItems];
      const newChatMessages = [...state.chatMessages];

      // Update or add assistant message in conversation items (OpenAI format)
      const apiMessage: ChatCompletionMessageParam = {
        role: "assistant",
        content: content,
      };

      if (
        newConversationItems.length > 0 &&
        newConversationItems[newConversationItems.length - 1].role ===
          "assistant"
      ) {
        newConversationItems[newConversationItems.length - 1] = apiMessage;
      } else {
        newConversationItems.push(apiMessage);
      }

      // Create the UI message content
      const messageContent: MessageContent = {
        type: "input_text",
        text: content,
      };

      // Update or add assistant message in chat messages (UI format)
      const uiMessage: MessageItem = {
        type: "message",
        role: "assistant",
        content: [messageContent],
      };

      if (
        newChatMessages.length > 0 &&
        newChatMessages[newChatMessages.length - 1].type === "message" &&
        (newChatMessages[newChatMessages.length - 1] as MessageItem).role ===
          "assistant"
      ) {
        newChatMessages[newChatMessages.length - 1] = uiMessage;
      } else {
        newChatMessages.push(uiMessage);
      }

      return {
        conversationItems: newConversationItems,
        chatMessages: newChatMessages,
      };
    }),
}));

export default useConversationStore;
